/**
 * TotalSales
 * --
 */
import { useState } from 'react';
import { Content, Title, Row, Col } from 'components';
import { Divider, Table, Spin } from 'antd';
import {
  CartesianGrid,
  Tooltip,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
} from 'recharts';
import { stringToMoneyFormat } from 'utils';
import moment from 'moment';

import './TotalSales.style.css';

const MOMENT_FORMAT = 'YYYY-MM-DD(HH:mm:ss)';
const today = moment().format(MOMENT_FORMAT);

const daysArray = [];
for (let i = 0; i < 7; i++) {
  const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
  daysArray.push(date);
}

/**----------------------------------------
 * [Component] 홈 > 매장별 매출 차트
 * ----------------------------------------
 */
const TotalSales = ({ list, mallList, tableList, isMobile }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  /**
   *
   * --
   */
  const totalList = list
    .sort((a, b) => {
      const aaa = moment(a.date).format('YYYY-MM-DD');
      const bbb = moment(b.date).format('YYYY-MM-DD');
      return aaa < bbb ? -1 : aaa > bbb ? 1 : 0;
    })
    .reduce((acc, cur) => {
      const d = moment(cur.date).format('YYYY-MM-DD');

      if (!acc || !acc[d]) {
        acc[d] = {
          totalAmount: 0,
          paymentCount: 0,
          netsAmount: 0,
          discountAmount: 0,
        };
      }
      acc[d]['totalAmount'] = acc[d]['totalAmount'] + Number(cur.totalAmount);
      acc[d]['paymentCount'] =
        acc[d]['paymentCount'] + Number(cur.paymentCount ?? 0);

      acc[d]['netsAmount'] = acc[d]['netsAmount'] + Number(cur.netsAmount);
      acc[d]['discountAmount'] =
        acc[d]['discountAmount'] + Number(cur.discountAmount);
      return acc;
    }, {});
  const dateList = mallList
    .sort((a, b) => {
      const aaa = moment(a.date).format('YYYY-MM-DD');
      const bbb = moment(b.date).format('YYYY-MM-DD');
      return aaa < bbb ? -1 : aaa > bbb ? 1 : 0;
    })
    .reduce((acc, cur) => {
      const d = moment(cur.date).format('YYYY-MM-DD');

      if (!acc || !acc[d]) {
        acc[d] = {
          totalAmount: 0,
          paymentCount: 0,
          netsAmount: 0,
          discountAmount: 0,
        };
      }
      acc[d]['totalAmount'] = acc[d]['totalAmount'] + Number(cur.totalAmount);
      acc[d]['paymentCount'] =
        acc[d]['paymentCount'] + Number(cur.paymentCount ?? 0);

      acc[d]['netsAmount'] = acc[d]['netsAmount'] + Number(cur.netsAmount);
      acc[d]['discountAmount'] =
        acc[d]['discountAmount'] + Number(cur.discountAmount);
      return acc;
    }, {});

  /**
   *
   * --
   */
  const data = dateList
    ? Object.keys(dateList).map((item) => ({
        date: item,
        ...dateList[item],
      }))
    : [];
  // console.log('data ===>', data);

  const data2 = totalList
    ? Object.keys(totalList).map((item) => ({
        date: item,
        ...totalList[item],
      }))
    : [];

  /* 매장목록 */
  const sales = tableList ? tableList.filter((item) => item.date) : [];
  const mapSales = new Map(
    sales.map((item) => [`${item.date}-${item.shop_information_id}`, item])
  );

  const sl3 = tableList
    ? Object.values(
        tableList.reduce((acc, cur) => {
          if (acc[cur.shop_information_id]) return acc;

          acc[cur.shop_information_id] = cur;

          return acc;
        }, {})
      )
    : [];

  const sl2 = sl3
    .filter((item) => !item.organization_id)
    .map((item) => ({
      ...item,
      children: [
        item,
        ...sl3.filter(
          (sub) => sub.organization_id === item.shop_information_id
        ),
      ].sort((a, b) => a.shop_information_id - b.shop_information_id),
    }))
    .sort((a, b) => a.shop_information_id - b.shop_information_id);

  const tableDataList = sl2.reduce((acc, cur) => {
    const { children } = cur;

    const newChildren = children.map((item) => {
      const newSales = daysArray.reduce((acc2, day) => {
        const getItem = mapSales.get(`${day}-${item.shop_information_id}`);
        acc2[day] = {
          totalAmount: getItem?.totalAmount ?? 0,
          netsAmount: getItem?.netsAmount ?? 0,
        };

        return acc2;
      }, {});

      return {
        ...item,
        ...newSales,
      };
    });

    const total = daysArray.reduce((acc2, cur2) => {
      const childrenTotal = newChildren.reduce(
        (acc3, cur3) => {
          acc3['totalAmount'] += cur3[cur2]
            ? Number(cur3[cur2]['totalAmount'])
            : 0;
          acc3['netsAmount'] += cur3[cur2]
            ? Number(cur3[cur2]['netsAmount'])
            : 0;

          return acc3;
        },
        { totalAmount: 0, netsAmount: 0 }
      );

      acc2[cur2] = childrenTotal;
      return acc2;
    }, {});

    const newData = {
      ...cur,
      ...total,
      children: newChildren.length > 1 ? newChildren : null,
    };

    acc.push(newData);
    return acc;
  }, []);
  // console.log('tableDataList ====>', tableDataList);

  const sl =
    tableList &&
    tableList
      .sort((a, b) => a.shop_information_id - b.shop_information_id)
      .reduce((acc, cur) => {
        if (!acc[cur.shop_information_name]) {
          acc[cur.shop_information_name] = [];
        }
        if (cur.date) {
          acc[cur.shop_information_name] = [
            ...acc[cur.shop_information_name],
            cur,
          ];
        }
        return acc;
      }, {});
  /* 날짜목록 */
  const dl =
    tableList &&
    [...tableList, ...data].reduce((acc, cur) => {
      if (!acc[cur.date]) {
        acc[cur.date] = {};
      }
      return acc;
    }, {});

  /*  */
  const ssl = {
    ['쇼핑몰']: data ?? [],
    ...sl,
  };
  const shopList = ssl ? Object.keys(ssl).map((item) => item) : [];

  /*  */
  const dayList = dl
    ? Object.keys(dl)
        .filter((item) => item !== null && item !== 'null')
        .map((item) => item)
        .sort()
        .reverse()
    : [];

  // console.log('[ ] shopList: ', shopList);
  // console.log('[ ] ssl: ', ssl);
  // console.log('[ ] dl: ', dl);
  // console.log('[ ] data: ', data);
  // console.log('[ ] dateList: ', dateList);
  // console.log('[ ] dayList: ', dayList);
  // console.log('[ ] dayList: ', dayList);

  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Row gutter={[5, 5]}>
          {/* ===== 차트: start ===== */}
          <Col x={isMobile ? 24 : 10}>
            <Content
              maxWidth={'100%'}
              borderRadius={4}
              padding={isMobile ? 10 : 20}
              height={isMobile ? 325 : 465}
              style={{ overflowY: 'auto' }}
            >
              <p size={5}>{today}기준</p>

              <ResponsiveContainer
                width="100%"
                minWidth={isMobile ? 'auto' : 400}
                height={isMobile ? 270 : 390}
                style={{ marginTop: 15 }}
              >
                <BarChart
                  // width={500}
                  // height={isMobile ? 260 : 390}
                  data={
                    !data2
                      ? []
                      : data2?.map((item) => ({
                          ...item,
                          date: moment(item.date).format('MM/DD'),
                          ['amt']: Number(item?.totalAmount ?? 0),
                          ['총매출']: Number(item?.totalAmount ?? 0),
                          ['순매출']: Number(item?.netsAmount ?? 0),
                          // ['순매출']: Number(item.netsAmount),
                        }))
                  }
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="6 7" />
                  {!isMobile && <YAxis dataKey="총매출" />}
                  <XAxis
                    type="category"
                    dataKey="date"
                    interval={isMobile ? 1 : 0}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="총매출" fill="#82ca9d" />
                  <Bar dataKey="순매출" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Content>
          </Col>

          {/* ===== 차트: end ===== */}
          {/* ===== 테이블: start ===== */}
          <Col x={isMobile ? 24 : 14}>
            <Content
              maxWidth={'100%'}
              height={475}
              borderRadius={4}
              padding={isMobile ? 10 : 20}
            >
              <Title
                size={5}
                style={{ marginBottom: 12, fontSize: isMobile ? 14 : 16 }}
              >
                전체 일별 매출
              </Title>
              <Divider style={{ margin: '12px 0' }} />

              {/* SECTION: tqtqtq */}
              <Table
                bordered
                size="small"
                scroll={{ x: 760, y: 290 }}
                pagination={false}
                dataSource={[
                  {
                    shop_information_name: '쇼핑몰',
                    ...daysArray.reduce((acc, cur) => {
                      const mapData = new Map(data.map((d) => [d.date, d]));
                      const d = mapData.get(cur);

                      acc[cur] = {
                        totalAmount: d?.totalAmount ?? 0,
                        netsAmount: d?.netsAmount ?? 0,
                      };

                      return acc;
                    }, {}),
                  },
                  ...tableDataList,
                ]}
                style={{
                  borderBottom: '1px solid #f1f1f1',
                  lineHeight: '115%',
                }}
                columns={[
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        매장＼날짜
                      </p>
                    ),
                    dataIndex: 'date',
                    width: isMobile ? `calc(100% / 4 - 24px)` : 120,
                    align: 'center',
                    fixed: 'left',
                    // ...(!isMobile && { fixed: 'left' }),
                    render: (_, v) => (
                      <p
                        style={{
                          lineHeight: '130%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        {v.shop_information_name}
                      </p>
                    ),
                  },
                  ...daysArray.map((item) => {
                    return {
                      title: (
                        <p
                          style={{
                            lineHeight: '135%',
                            fontSize: isMobile ? '0.7em' : '1em',
                          }}
                        >
                          {item}
                          <br />
                          총매출(순매출)
                        </p>
                      ),
                      dataIndex: `${item}`,
                      width: isMobile ? `calc(100% / 4 - 24px)` : 130,
                      align: 'center',
                      render: (_, v) => {
                        return (
                          <p
                            style={{
                              lineHeight: '125%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            {stringToMoneyFormat(
                              Number(v[item]['totalAmount'] ?? 0)
                            )}
                            <br />
                            <small>
                              (
                              {stringToMoneyFormat(
                                Number(v[item]['netsAmount'] ?? 0)
                              )}
                              )
                            </small>
                          </p>
                        );
                      },
                    };
                  }),
                ]}
                summary={(record) => {
                  const summary = daysArray.reduce((acc, cur) => {
                    let amt = 0,
                      netsAmt = 0;

                    record.forEach((item) => {
                      amt += item[cur]['totalAmount'];
                      netsAmt += item[cur]['netsAmount'];
                    });

                    acc.push({ totalAmount: amt, netsAmount: netsAmt });

                    return acc;
                  }, []);

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row align="center">
                        <Table.Summary.Cell index={0}>
                          <b
                            style={{
                              lineHeight: '125%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            합계
                          </b>
                        </Table.Summary.Cell>

                        {summary.map((item, index) => (
                          <Table.Summary.Cell
                            index={index + 1}
                            key={`summary_${index}`}
                          >
                            <p
                              style={{
                                lineHeight: '125%',
                                fontSize: isMobile ? '0.7em' : '1em',
                              }}
                            >
                              {stringToMoneyFormat(item.totalAmount)}
                              <br />({stringToMoneyFormat(item.netsAmount)})
                            </p>
                          </Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
                expandable={{
                  rowExpandable: (record) => record.children.length,
                  expandedRowClassName: 'expandable-children',
                }}
              />
              {/* !SECTION: tqtqtq */}
            </Content>
          </Col>
          {/* ===== 테이블: end ===== */}
        </Row>
      </Spin>
    </>
  ); // JSX: end
};

TotalSales.defaultProps = {
  storeSales: [],
  orderList: [],
  list: [],
  // dateList: [],
  // dateList: [],
};
export default TotalSales;
